import { chartCssVars, useChart, useYScale } from "./chart-context";

const DEFAULT_GROUP_GAP = 4;

/**
 * Per-point value labels for a series — Lodgeic extension replacing
 * recharts `<LabelList>`. Works inside BarChart (band scale, grouped or
 * stacked) and the time-series charts (Line/Area/Composed).
 *
 * `position="top"` floats labels above the point/bar; `position="inside"`
 * tucks them just below a bar's top edge.
 */
export function ValueLabels({
  dataKey,
  yAxisId,
  formatter,
  fill = chartCssVars.label,
  fontSize = 10,
  fontFamily,
  fontWeight,
  position = "top",
  dx = 0,
  dy = 0,
  minLabelValue,
}) {
  const {
    data,
    yScale,
    lines,
    barScale,
    bandWidth,
    barXAccessor,
    stacked,
    stackOffsets,
    xScale,
    xAccessor,
    innerWidth,
  } = useChart();
  const axisScale = useYScale(yAxisId) ?? yScale;

  if (!dataKey || !data?.length || !axisScale) {
    return null;
  }

  const format = formatter ?? ((value) => String(value));
  const isBarChart = Boolean(barScale && bandWidth != null);

  const seriesIndex = Math.max(
    0,
    lines.findIndex((line) => line.dataKey === dataKey)
  );
  const seriesCount = lines.length;

  const labels = data.map((d, i) => {
    const value = d[dataKey];
    if (typeof value !== "number") {
      return null;
    }
    if (minLabelValue != null && Math.abs(value) < minLabelValue) {
      return null;
    }

    let px;
    let py;

    if (isBarChart) {
      const pos = barXAccessor ? barScale(barXAccessor(d)) : null;
      if (pos == null) {
        return null;
      }
      if (stacked) {
        const offset = stackOffsets?.get(i)?.get(dataKey) ?? 0;
        px = pos + bandWidth / 2;
        py = axisScale(offset + value) ?? 0;
      } else {
        const groupGap = seriesCount > 1 ? DEFAULT_GROUP_GAP : 0;
        const barW =
          seriesCount > 0
            ? (bandWidth - groupGap * (seriesCount - 1)) / seriesCount
            : bandWidth;
        px = pos + seriesIndex * (barW + groupGap) + barW / 2;
        py = axisScale(value) ?? 0;
      }
      const isNegative = value < 0;
      if (position === "inside") {
        py += isNegative ? -4 : 12;
      } else {
        py += isNegative ? 12 : -5;
      }
    } else {
      if (!xScale || !xAccessor) {
        return null;
      }
      px = xScale(xAccessor(d)) ?? 0;
      py = (axisScale(value) ?? 0) + (position === "inside" ? 12 : -8);
    }

    const text = format(value, d, i);

    // Clamp labels inside the plot so first/last/top points don't clip at
    // the chart edges (recharts drew into overflow; the kit shells don't).
    if (typeof innerWidth === "number" && innerWidth > 0) {
      const halfWidth = String(text).length * fontSize * 0.31;
      px = Math.min(Math.max(px, halfWidth + 2), innerWidth - halfWidth - 2);
    }
    if (position !== "inside") {
      py = Math.max(py, fontSize + 2);
    }

    return (
      <text
        fill={fill}
        fontSize={fontSize}
        fontWeight={fontWeight}
        key={`vl-${dataKey}-${i}`}
        style={{ fontFamily, fontVariantNumeric: "tabular-nums" }}
        textAnchor="middle"
        x={px + dx}
        y={py + dy}>
        {text}
      </text>
    );
  });

  return <g pointerEvents="none">{labels}</g>;
}

ValueLabels.displayName = "ValueLabels";

export default ValueLabels;
